/**
 * Dashboard query utilities.
 *
 * Handles placeholder replacement and request URL/param building.
 * All parameter construction uses URLSearchParams to avoid injection.
 */

/**
 * Compute the offset period for inclusive day-aligned histogram ranges.
 * For a period like "7d", returns "6d" so that `now/d-6d TO now` yields
 * exactly 7 calendar-day buckets (days −6 through today).
 * Non-day periods (e.g. "12w") are returned unchanged.
 *
 * @param {string} period - e.g. "7d" or "30d"
 * @returns {string} - e.g. "6d"
 */
export function periodToOffset(period) {
  const match = period && period.match(/^(\d+)d$/);
  if (!match) return period;
  const n = parseInt(match[1], 10);
  return n <= 1 ? '0d' : `${n - 1}d`;
}

/**
 * Replace {{period}}, {{prevPeriod}}, {{periodLabel}}, and {{periodOffset}}
 * placeholders in a string.
 *
 * @param {string} str - The string to process.
 * @param {string} period - e.g. "30d"
 * @param {string} prevPeriod - e.g. "60d"
 * @param {string} [periodLabel] - e.g. "Today" or "30d"; falls back to period if omitted.
 * @returns {string}
 */
export function replacePeriodPlaceholders(str, period, prevPeriod, periodLabel) {
  if (!str) return str;

  // Compute {{dateFrom}} and {{dateTo}} from the period value.
  // Custom period format: "custom:YYYY-MM-DD:YYYY-MM-DD"
  // Regular period format: "30d", "7d", etc.
  let dateFrom, dateTo;
  const customMatch = period?.match(/^custom:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/);
  if (customMatch) {
    dateFrom = customMatch[1];
    dateTo = customMatch[2];
  } else {
    // For day-based periods (e.g. "7d"), use day-aligned boundaries so that
    // "7d" means exactly 7 calendar days (today + 6 previous), not 168 hours.
    // now/d-6d = start of 6 days ago → now = 7 calendar days inclusive.
    const isDayPeriod = period && /^\d+d$/.test(period);
    dateFrom = period ? (isDayPeriod ? `now/d-${periodToOffset(period)}` : `now-${period}`) : '';
    dateTo = 'now';
  }

  return str
    .replaceAll('{{period}}', period)
    .replaceAll('{{prevPeriod}}', prevPeriod)
    .replaceAll('{{periodLabel}}', periodLabel ?? period)
    .replaceAll('{{periodOffset}}', periodToOffset(period))
    .replaceAll('{{dateFrom}}', dateFrom)
    .replaceAll('{{dateTo}}', dateTo);
}

/**
 * Apply period placeholders to all string values in a params object.
 *
 * @param {Record<string, any>} params
 * @param {string} period
 * @param {string} prevPeriod
 * @returns {Record<string, any>}
 */
export function applyPeriodToParams(params, period, prevPeriod) {
  if (!params) return {};
  const result = {};
  for (const [key, value] of Object.entries(params)) {
    result[key] = typeof value === 'string' ? replacePeriodPlaceholders(value, period, prevPeriod) : value;
  }
  return result;
}

const WIDGET_PERIOD_STRING_KEYS = ['name', 'linkLabel'];
const LINK_OBJECT_STRING_KEYS = ['to', 'href', 'url', 'label'];

/**
 * Replace period placeholders in a widget `link` field.
 * Accepts a string path or an object with to/href/url/label.
 *
 * @param {string | Record<string, any>} link
 * @param {string} period
 * @param {string} prevPeriod
 * @param {string} [periodLabel]
 * @returns {string | Record<string, any>}
 */
function applyPeriodToLink(link, period, prevPeriod, periodLabel) {
  if (typeof link === 'string') {
    return replacePeriodPlaceholders(link, period, prevPeriod, periodLabel);
  }
  if (!link || typeof link !== 'object') return link;

  const next = { ...link };
  for (const key of LINK_OBJECT_STRING_KEYS) {
    if (typeof next[key] === 'string') {
      next[key] = replacePeriodPlaceholders(next[key], period, prevPeriod, periodLabel);
    }
  }
  return next;
}

/**
 * Replace period placeholders on widget display strings (title/name, link, linkLabel)
 * and nested breakdown items. Query `params` are handled separately by applyPeriodToParams.
 *
 * @param {Record<string, any>} widget
 * @param {string} period
 * @param {string} prevPeriod
 * @param {string} [periodLabel]
 * @returns {Record<string, any>}
 */
export function applyPeriodToWidget(widget, period, prevPeriod, periodLabel) {
  if (!widget || typeof widget !== 'object') return widget;

  const next = { ...widget };

  for (const key of WIDGET_PERIOD_STRING_KEYS) {
    if (typeof next[key] === 'string') {
      next[key] = replacePeriodPlaceholders(next[key], period, prevPeriod, periodLabel);
    }
  }

  if (next.link != null) {
    next.link = applyPeriodToLink(next.link, period, prevPeriod, periodLabel);
  }

  if (Array.isArray(next.items)) {
    next.items = next.items.map((item) => applyPeriodToWidget(item, period, prevPeriod, periodLabel));
  }

  return next;
}

/**
 * Build a URL with query parameters.
 * Omits params with null/undefined values.
 *
 * @param {string} baseUrl - e.g. "/admin/users"
 * @param {Record<string, any>} params
 * @returns {string} - e.g. "/admin/users?metaonly=true&query=..."
 */
export function buildUrl(baseUrl, params) {
  if (!params || Object.keys(params).length === 0) return baseUrl;
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      sp.set(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
}

/**
 * Resolve the prevPeriod for a given period string.
 * Expects format like "30d", "7d", "1d", "90d".
 * Doubles the numeric value and keeps the unit.
 *
 * @param {string} period - e.g. "30d"
 * @param {{ value: string, prevPeriod: string }[]} periods - period config list
 * @returns {string}
 */
export function resolvePrevPeriod(period, periods) {
  if (!period || period.startsWith('custom:')) return '';
  const found = periods?.find((p) => p.value === period);
  if (found?.prevPeriod) return found.prevPeriod;

  // Fallback: double the numeric part
  const match = period.match(/^(\d+)([a-z]+)$/i);
  if (match) {
    return `${Number(match[1]) * 2}${match[2]}`;
  }
  return period;
}

/**
 * Generate a stable string ID for a widget based on section/widget names.
 *
 * @param {string} sectionName
 * @param {string} widgetName
 * @param {string} [suffix]
 * @returns {string}
 */
export function widgetId(sectionName, widgetName, suffix = '') {
  const base = `${sectionName}__${widgetName}`.toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  return suffix ? `${base}__${suffix}` : base;
}
